import { isBrowser } from './browserHelper';
import {
  type AppViewParsedQueryParams,
  type AppViewQueryParamKeys,
  getAppViewQueryParamNames,
  getAppViewQueryParams,
} from './appViewHelper';
import type { AppPage, AppView, OperationPage } from '../types/app';

export const SERVICE_AREA_SUB_PAGES = {
  CONFIRM: 'confirm',
  CONTACT: 'contact',
  SERVICES: 'services',
  TIME_SLOTS: 'timeslots',
} as const;

const BOKA_TEST_SERVICE_AREA = 'takingataxitothenewtestenvironment';
export const TIMESLOTSTODAY = 'timeslotstoday';
const BOKA_BASE_PATH = `/${CSS_CONFIG.country}/${CSS_CONFIG.language}/appointment`;

export const isBokaTestServiceURL = () => {
  if (!isBrowser()) return false;
  return window.location.pathname.includes(BOKA_TEST_SERVICE_AREA);
};
export const getBokaSubPathname = (
  pathname: string,
  subPath: typeof SUB_PATH,
  view: AppView,
): string => {
  let pathSuffix = '';
  if (view === 'alt') {
    pathSuffix = 'alt/';
  } else if (view === 'app' && pathname.split('/').some((s) => s === 'app')) {
    pathSuffix = 'app/';
  }
  const path = `${BOKA_BASE_PATH}${subPath}/${pathSuffix}`;
  return pathname.substring(path.length);
};

/* Information about the query parameters used in this appplication (both EXTERNAL and BOKA) can be found here:
https://confluence.build.ingka.ikea.com/pages/viewpage.action?pageId=817044873&spaceKey=MSS&title=Customer%2Bquery%2Bparameters
*/
const EXTERNAL_QUERY_PARAMS = {
  VPC: 'vpc',
  PROJECT_ID: 'projectid',
  IMAGINATION: 'imagination',
  COOKIE_CONSENT_OVERRIDE: 'cookieconsent-override',
};

export const BOKA_QUERY_PARAMS = {
  TOKEN: 'token',
  ZIP: 'zip',
  SERVICE: 'service',
  OPERATION: 'operation',
  STORE: 'store',
  REFAPP: 'ref_app',
  EVENT_ACTION: 'event_action',
  STORESELECTOR: 'storeselector',
  TRANSLATIONSKEYS: 'translationskeys',
  TEST_MODE: 'test_mode',
  ENABLE_BOKA_FEATURE: 'enablebokafeature',
  WALKIN: 'walkin',
};

interface ParsedQueryParams extends AppViewParsedQueryParams {
  questionsToken: string;
  zip: string;
  service: string;
  vpc: string;
  projectId: string;
  imagination: string;
  eventAction: string;
  operation: string;
  store: string;
  refApp: string;
  storeSelector: boolean;
  translationsKeys: boolean;
  walkin: boolean;
}

const ALPHA_NUMERIC_DASH_UNDERSCORE_REGEX = /[^a-zA-Z0-9_-]/g;

export const sanitizeInput = (param: string): string => {
  return param.replace(ALPHA_NUMERIC_DASH_UNDERSCORE_REGEX, '');
};

export const getQueryParamsFromURL = (): ParsedQueryParams => {
  const {
    SERVICE,
    ZIP,
    TOKEN,
    OPERATION,
    STORE,
    REFAPP,
    EVENT_ACTION,
    STORESELECTOR,
    TRANSLATIONSKEYS,
    WALKIN,
  } = BOKA_QUERY_PARAMS;
  const { VPC, PROJECT_ID, IMAGINATION } = EXTERNAL_QUERY_PARAMS;
  const queryString = isBrowser() ? window.location.search : '';
  const queryParams = new URLSearchParams(queryString);

  const questionsToken = queryParams.get(TOKEN) || '';
  const zip = queryParams.get(ZIP) || '';
  const service = queryParams.get(SERVICE) || '';
  const vpc = sanitizeInput(queryParams.get(VPC) || '');
  const projectId = sanitizeInput(queryParams.get(PROJECT_ID) || '');
  const imagination = sanitizeInput(queryParams.get(IMAGINATION) || '');
  const eventAction = sanitizeInput(queryParams.get(EVENT_ACTION) || '');
  const operation = queryParams.get(OPERATION) || '';
  const store = queryParams.get(STORE) || '';
  const refApp = sanitizeInput(queryParams.get(REFAPP) || '');
  const storeSelector =
    queryParams.get(STORESELECTOR) === 'false' ? false : true;
  const translationsKeys =
    queryParams.get(TRANSLATIONSKEYS) === 'true' ? true : false;
  const walkin = queryParams.get(WALKIN) === 'true' ? true : false;
  const appViewParams = getAppViewQueryParams();

  return {
    questionsToken,
    zip,
    service,
    vpc,
    projectId,
    imagination,
    eventAction,
    operation,
    store,
    refApp,
    storeSelector,
    translationsKeys,
    walkin,
    ...appViewParams,
  };
};

type PersistingQueryParamKeys =
  | 'vpc'
  | 'projectid'
  | 'imagination'
  | 'ref_app'
  | 'event_action'
  | 'cookieconsent-override'
  | 'storeselector'
  | 'translationskeys'
  | 'test_mode'
  | 'walkin'
  | AppViewQueryParamKeys;

/**
 * Partner-tracking query params to strip when redirecting to error pages
 * (service-unavailable / 404).
 */
export const ERROR_REDIRECT_EXCLUDE_PARAMS: PersistingQueryParamKeys[] = [
  'vpc',
  'projectid',
  'imagination',
  'event_action',
];

const filterQueryParams = (
  queryParams: URLSearchParams,
  exclude: PersistingQueryParamKeys[],
) => {
  const keys = [...queryParams.keys()];
  const persistingQueryParams = [
    ...Object.values(EXTERNAL_QUERY_PARAMS),
    BOKA_QUERY_PARAMS.REFAPP,
    BOKA_QUERY_PARAMS.EVENT_ACTION,
    BOKA_QUERY_PARAMS.STORESELECTOR,
    BOKA_QUERY_PARAMS.TRANSLATIONSKEYS,
    BOKA_QUERY_PARAMS.TEST_MODE,
    BOKA_QUERY_PARAMS.ENABLE_BOKA_FEATURE,
    BOKA_QUERY_PARAMS.WALKIN,
    ...getAppViewQueryParamNames(),
  ];

  keys.forEach((key) => {
    if (
      !persistingQueryParams.includes(key) ||
      (exclude as string[]).includes(key)
    ) {
      queryParams.delete(key);
    }
  });
};

type AddQueryParamsToPathOptions = {
  addToExistingQueryParam?: boolean;
  exclude?: PersistingQueryParamKeys[];
};

export const addPersistingQueryParamsToPath = (
  path: string,
  options?: AddQueryParamsToPathOptions,
): string => {
  if (!isBrowser()) {
    return path;
  }

  const { addToExistingQueryParam = false, exclude = [] } = options || {};
  const queryParams = new URLSearchParams(window.location.search);
  filterQueryParams(queryParams, exclude);
  const queryParamsString = queryParams.toString();

  return queryParamsString
    ? `${path}${addToExistingQueryParam ? '&' : '?'}${queryParamsString}`
    : path;
};

/* ikeaapp cleanup: Remove once the app team starts using /app instead of /ikeaapp */
const getAppViewPrefix = (page?: string): string => {
  if (isBrowser() && window.location.pathname.includes(`/ikeaapp/`)) {
    return page === 'questions-confirmed' ? 'ikeaapp' : '';
  }
  return 'app';
};

const getViewPrefix = (view: AppView, page?: string): string => {
  if (view === 'alt') return 'alt';
  if (view === 'app') return getAppViewPrefix(page);
  return '';
};

export const getPrevPath = (operation: string, view: AppView): string => {
  const currentUrl = window.location.pathname;

  if (currentUrl.includes('/followup/')) {
    if (currentUrl.endsWith('/timeslots/')) return '/followup/contact/';
    if (currentUrl.endsWith('/contact/')) return '/followup/timeslots/';
    if (currentUrl.endsWith('/confirm/')) return '/followup/contact/';
  }

  let prevPage = '';
  if (currentUrl.endsWith('/contact/')) {
    prevPage = 'timeslots/';
  } else if (currentUrl.endsWith('/timeslots/')) {
    prevPage = 'services/';
  } else if (currentUrl.endsWith('/services/')) {
    prevPage = '';
  }

  const prefix = getViewPrefix(view);
  return prefix
    ? `/${prefix}/${operation}/${prevPage}`
    : `/${operation}/${prevPage}`;
};

export const getPath = (
  operation: string,
  step: string,
  view: AppView,
): string => {
  if (window.location.pathname.includes('/followup/')) {
    return `/followup/${step}/`;
  }
  const prefix = getViewPrefix(view);
  return prefix ? `/${prefix}/${operation}/${step}/` : `/${operation}/${step}/`;
};

export const getPagePath = (page: string, view: AppView): string => {
  const prefix = getViewPrefix(view, page);
  return prefix ? `/${prefix}/${page}/` : `/${page}/`;
};

export const getAppPageFromPath = (path: string): AppPage => {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'followup') {
    if (segments[1] === 'timeslots') return 'followup-timeslots';
    if (segments[1] === 'contact') return 'followup-contact';
    if (segments[1] === 'confirm') return 'followup-confirm';
  }
  const page = segments.length > 0 ? segments[segments.length - 1] : '';
  if (
    page === 'services' ||
    page === 'timeslots' ||
    page === 'contact' ||
    page === 'confirm' ||
    page === 'questions' ||
    page === 'questions-confirmed' ||
    page === 'service-unavailable'
  ) {
    return page;
  }
  if (page === '404' || page === 'appointment') {
    return 'not-found';
  }
  return 'start';
};

export const getOperationFromPath = (
  page: OperationPage,
  path: string,
): string => {
  const segments = path.split('/').filter(Boolean);
  const lastIndex = segments.length - 1;
  if (page === 'start') {
    return segments[lastIndex];
  }
  return segments[lastIndex - 1];
};
